import React from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react'
import { orderBy } from 'lodash'
import { Id } from 'types/definitions'
import {
  Card,
  CardHeader,
  CardTitle,
  CardTitleCount,
  CardHeaderActions,
  CardContent,
} from 'components/Card'
import JobViewModel from '../JobViewModel'
import { SectionError, SectionLoading } from '../Section/Section'
import styles from './JobScopeChangeRequestsCard.sass'
import JobScopeChangeRequest from 'jobs/JobScopeChange'
import Avatar, { Badge } from 'components/Avatar'
import {
  Dropdown,
  DropdownItem,
  DropdownItemTitle,
  DropdownTitle,
} from 'components/Dropdown'
import { IconButton } from 'components/IconButton'
import Icon from 'components/Icon'
import { useToggle } from 'hooks/state-hooks'
import Button from 'components/Button'

interface JobScopeChangeRequestsProps {
  vm: JobViewModel
}

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour12: true,
  hour: 'numeric',
  minute: '2-digit',
})

export default observer(function JobScopeChangeRequestsCard({
  vm,
}: JobScopeChangeRequestsProps) {
  const scopeChangeRequests = orderBy(
    vm.scopeChangeRequests,
    'createTime',
    'desc',
  )

  return (
    <Card dataTest="job-scope-change-requests-card">
      <CardHeader>
        <CardTitle>
          Job scope updates{' '}
          <CardTitleCount>({scopeChangeRequests?.length || 0})</CardTitleCount>
        </CardTitle>

        <CardHeaderActions>
          {(vm.canRequestScopeChanges || vm.canUpdateJobScope) && (
            <ScopeChangeRequestsActionsDropdown vm={vm} />
          )}
        </CardHeaderActions>
      </CardHeader>

      <CardContent scroll compact>
        {vm.listJobScopeChanges.match({
          pending: () => <SectionLoading />,
          rejected: (err) => (
            <SectionError
              title="An error occurred while loading scope change requests."
              error={err}
            />
          ),
          resolved: () =>
            scopeChangeRequests.map((scopeChangeRequest) => (
              <ScopeChangeRequest
                key={Id.assert(scopeChangeRequest.id)}
                scopeChangeRequest={scopeChangeRequest}
                viewDetail={vm.viewScopeChangeRequests}
              />
            )),
        })}
      </CardContent>
    </Card>
  )
})

interface ScopeChangeRequestProps {
  scopeChangeRequest: JobScopeChangeRequest
  viewDetail: (scopeChangeRequest: JobScopeChangeRequest) => void
}

const ScopeChangeRequest = observer(
  ({ scopeChangeRequest, viewDetail }: ScopeChangeRequestProps) => {
    return (
      <div
        className={cx(styles.root)}
        data-test={`job-scope-change-request-${scopeChangeRequest.status}`}
      >
        <div className={styles.container}>
          <div className={styles.avatarContainer}>
            <Avatar
              size={32}
              backgroundColor={
                scopeChangeRequest.isPending
                  ? 'var(--brand-primary)'
                  : 'var(--grey500)'
              }
              badge={
                scopeChangeRequest.isAccepted
                  ? Badge.CheckMark
                  : scopeChangeRequest.isRejected ||
                      scopeChangeRequest.isApplyFailed
                    ? Badge.ErrorMark
                    : Badge.None
              }
              subject={{
                avatar: require('assets/logos/logo_worklayer-white.svg'),
              }}
            />
          </div>

          <div className={styles.content}>
            <p className={styles.description}>
              <strong>
                {scopeChangeRequest.isCreatedByAdmin &&
                (scopeChangeRequest.isPending ||
                  scopeChangeRequest.isRejected ||
                  scopeChangeRequest.isApplyFailed)
                  ? 'An admin has identified services that fall outside the original job scope.'
                  : scopeChangeRequest.isCreatedByAdmin &&
                      scopeChangeRequest.isAccepted
                    ? 'An admin has updated the job scope.'
                    : 'You have identified services that fall outside the original job scope.'}
              </strong>
            </p>

            <div className={styles.date}>
              {scopeChangeRequest.isAccepted && (
                <>
                  Approved on{' '}
                  {dateFormatter.format(
                    scopeChangeRequest.latestResponseTime ??
                      scopeChangeRequest.createTime,
                  )}
                </>
              )}

              {scopeChangeRequest.isRejected && (
                <>
                  Declined on{' '}
                  {dateFormatter.format(scopeChangeRequest.latestResponseTime)}
                </>
              )}

              {scopeChangeRequest.isApplyFailed && (
                <>
                  System rejected on{' '}
                  {dateFormatter.format(scopeChangeRequest.latestResponseTime)}
                </>
              )}

              {scopeChangeRequest.isPending && (
                <strong
                  style={{
                    color: 'var(--brand-primary)',
                  }}
                >
                  Pending review
                </strong>
              )}
            </div>
          </div>

          <Button onClick={() => viewDetail(scopeChangeRequest)}>View</Button>
        </div>
      </div>
    )
  },
)

interface ScopeChangeRequestsActionsDropdownProps {
  vm: JobViewModel
  showUpdateJobScopeOption?: boolean
}

const ScopeChangeRequestsActionsDropdown = observer(
  ({ vm }: ScopeChangeRequestsActionsDropdownProps) => {
    const dropdownOpened = useToggle(false)
    const toggle = dropdownOpened.toggle
    const showUpdateJobScopeOption = vm.canUpdateJobScope

    return (
      <Dropdown
        width="medium"
        isOpen={dropdownOpened.isEnabled}
        onRequestClose={dropdownOpened.disable}
        position="bottom-end"
        button={
          <div className={styles.moreButton} onClick={toggle}>
            <IconButton>
              <Icon name="more" />
            </IconButton>
          </div>
        }
        dataTest="job-scope-change-requests-card-dropdown"
      >
        <DropdownTitle>Actions</DropdownTitle>
        {showUpdateJobScopeOption ? (
          <DropdownItem
            relative
            onClick={vm.updateJobScopeVM.show}
            data-test="update-job-scope-card-dropdown-item"
          >
            <DropdownItemTitle>Update job scope</DropdownItemTitle>
          </DropdownItem>
        ) : (
          <DropdownItem
            relative
            onClick={vm.requestScopeChangesVM.show}
            data-test="request-scope-change-card-dropdown-item"
          >
            <DropdownItemTitle>Request scope changes</DropdownItemTitle>
          </DropdownItem>
        )}
      </Dropdown>
    )
  },
)
